export const DAILY_FREEMIUM_ALLOWANCE_MINUTES = 10;
export const WEEKLY_QUALIFIED_CONVERSATION_ALLOWANCE = 3;
export const QUALIFIED_CONVERSATION_MIN_MESSAGES = 10;
export const MEDIUM_QUALIFIED_CONVERSATION_THRESHOLD = 5;
export const HEAVY_QUALIFIED_CONVERSATION_THRESHOLD = 10;
export const MEDIUM_FREEMIUM_DISCOUNT_COUPON = "progress33";
export const HEAVY_FREEMIUM_DISCOUNT_COUPON = "momentum33";

export type FreemiumCohort =
  | "light_unconverted"
  | "medium_unconverted"
  | "heavy_unconverted"
  | "subscribed";
export type FreemiumOfferMode = "trial" | "discount";

export interface ConversationUsageRow {
  updated_at?: string | null;
  updatedAt?: string | number | null;
  message_count?: number | string | null;
  conversation_messages?: ConversationMessageCountAggregate;
  messages?: unknown;
}

type ConversationMessageCountAggregate =
  | { count?: number | string | null }
  | Array<{ count?: number | string | null }>
  | null;

export interface FreemiumUsageState {
  qualifiedConversationCount: number;
  qualifiedConversationsThisWeek: number;
  dailyAllowanceMinutes: number;
  weeklyConversationAllowance: number;
  dailyRemainingMinutes: number;
  weeklyRemainingConversations: number;
  cohort: FreemiumCohort;
  offerMode: FreemiumOfferMode;
  shouldShowPreSessionOffer: boolean;
}

function parseMessageCount(value: number | string | null | undefined): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string") {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function getEmbeddedConversationMessageCount(
  aggregate: ConversationMessageCountAggregate | undefined
): number | null {
  const countSource = Array.isArray(aggregate) ? aggregate[0] : aggregate;
  return parseMessageCount(countSource?.count);
}

export function getMondayWeekStart(now = new Date()): Date {
  const d = new Date(now);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function getConversationMessageCount(row: ConversationUsageRow): number {
  const directCount = parseMessageCount(row.message_count);
  if (directCount !== null) return directCount;

  const embeddedCount = getEmbeddedConversationMessageCount(row.conversation_messages);
  if (embeddedCount !== null) return embeddedCount;

  return Array.isArray(row.messages) ? row.messages.length : 0;
}

export function isQualifiedConversation(row: ConversationUsageRow): boolean {
  return getConversationMessageCount(row) >= QUALIFIED_CONVERSATION_MIN_MESSAGES;
}

export function getFreemiumDiscountCoupon(cohort?: FreemiumCohort | null): string | null {
  if (cohort === "heavy_unconverted") return HEAVY_FREEMIUM_DISCOUNT_COUPON;
  if (cohort === "medium_unconverted") return MEDIUM_FREEMIUM_DISCOUNT_COUPON;
  return null;
}

export function isFreemiumDiscountCohort(cohort?: FreemiumCohort | null): boolean {
  return getFreemiumDiscountCoupon(cohort) !== null;
}

function getConversationUpdatedAt(row: ConversationUsageRow): Date | null {
  const raw = row.updated_at ?? row.updatedAt;
  if (!raw) return null;

  const date = typeof raw === "number" ? new Date(raw) : new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function calculateFreemiumUsageState(
  conversations: ConversationUsageRow[],
  options?: {
    now?: Date;
    dailyRemainingMinutes?: number;
    subscribed?: boolean;
  }
): FreemiumUsageState {
  if (options?.subscribed) {
    return {
      qualifiedConversationCount: 0,
      qualifiedConversationsThisWeek: 0,
      dailyAllowanceMinutes: DAILY_FREEMIUM_ALLOWANCE_MINUTES,
      weeklyConversationAllowance: WEEKLY_QUALIFIED_CONVERSATION_ALLOWANCE,
      dailyRemainingMinutes: DAILY_FREEMIUM_ALLOWANCE_MINUTES,
      weeklyRemainingConversations: WEEKLY_QUALIFIED_CONVERSATION_ALLOWANCE,
      cohort: "subscribed",
      offerMode: "trial",
      shouldShowPreSessionOffer: false,
    };
  }

  const now = options?.now ?? new Date();
  const weekStart = getMondayWeekStart(now);
  const qualified = conversations.filter(isQualifiedConversation);
  const qualifiedThisWeek = qualified.filter((row) => {
    const updatedAt = getConversationUpdatedAt(row);
    return updatedAt ? updatedAt >= weekStart && updatedAt <= now : false;
  });

  const cohort =
    qualified.length >= HEAVY_QUALIFIED_CONVERSATION_THRESHOLD
      ? "heavy_unconverted"
      : qualified.length >= MEDIUM_QUALIFIED_CONVERSATION_THRESHOLD
        ? "medium_unconverted"
        : "light_unconverted";
  const weeklyRemainingConversations = Math.max(
    0,
    WEEKLY_QUALIFIED_CONVERSATION_ALLOWANCE - qualifiedThisWeek.length
  );
  const dailyRemainingMinutes = Math.max(
    0,
    Math.min(
      DAILY_FREEMIUM_ALLOWANCE_MINUTES,
      options?.dailyRemainingMinutes ?? DAILY_FREEMIUM_ALLOWANCE_MINUTES
    )
  );

  return {
    qualifiedConversationCount: qualified.length,
    qualifiedConversationsThisWeek: qualifiedThisWeek.length,
    dailyAllowanceMinutes: DAILY_FREEMIUM_ALLOWANCE_MINUTES,
    weeklyConversationAllowance: WEEKLY_QUALIFIED_CONVERSATION_ALLOWANCE,
    dailyRemainingMinutes,
    weeklyRemainingConversations,
    cohort,
    offerMode: isFreemiumDiscountCohort(cohort) ? "discount" : "trial",
    shouldShowPreSessionOffer:
      isFreemiumDiscountCohort(cohort) &&
      dailyRemainingMinutes > 0 &&
      weeklyRemainingConversations > 0,
  };
}
